import { ComponentDesign, isTruss, PlatePair } from '@paragon/contract-library';
import { Point2D, Polygon2D } from '@paragon/geometry-library';
import { getMostCommonPlateType, getPlateTypeString } from '@paragon/material-lists';
import { calculateMemberPolygon } from '@paragon/three-shared';

import { getPlateDimensionOptions } from '../../components/interface/subpanels/properties/PlatePairProperties';
import type { AppDispatch, RootState } from '../../store';
import { deleteItems } from '../../store/actions/deletionActions';
import { changePlatePairs } from '../../store/actions/editPlatePairActions';
import {
  cancelInsertion,
  startInsertPlatePair,
  updateAndPotentiallyCommitInsertionPlatePair,
} from '../../store/actions/insertActions';
import { getUniquePlatesInMaterialSetForActiveComponentDesign } from '../../store/selectors/getUniquePlatesInPlateList';
import { describeMemberGeometry, getTrussMembers, openTruss, requireString, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };
const POINT_SCHEMA = {
  type: 'object',
  properties: { x: { type: 'number' }, y: { type: 'number' } },
  required: ['x', 'y'],
};
const SIZE_SCHEMA = {
  type: 'string',
  description: 'Width by length in inches, as drawings give it, such as "4x7". get_plates lists the sizes in stock.',
};
const ANGLE_SCHEMA = {
  type: 'number',
  description: 'The direction the plate’s length runs, in degrees counter-clockwise from horizontal.',
};

const round = (value: number) => Math.round(value * 16) / 16;
const roundAngle = (degrees: number) => Math.round((((degrees % 360) + 360) % 360) * 10) / 10;

/** The plate's outline on the truss: its length along its angle, its width across. */
export function getPlateOutline(plate: Pick<PlatePair, 'centerX' | 'centerY' | 'angleZ' | 'width' | 'length'>) {
  const radians = (plate.angleZ * Math.PI) / 180;
  const along = { x: (Math.cos(radians) * plate.length) / 2, y: (Math.sin(radians) * plate.length) / 2 };
  const across = { x: (-Math.sin(radians) * plate.width) / 2, y: (Math.cos(radians) * plate.width) / 2 };
  return [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ].map(
    ([a, b]) => new Point2D(plate.centerX + a * along.x + b * across.x, plate.centerY + a * along.y + b * across.y),
  );
}

/** The members a plate covers, with the direction each runs, so a plate can be turned to line up with one. */
function getMembersUnderPlate(componentDesign: ComponentDesign, plate: PlatePair) {
  const { component } = componentDesign;
  const outline = new Polygon2D(getPlateOutline(plate), false);
  return getTrussMembers(componentDesign).flatMap((member) => {
    const polygon = calculateMemberPolygon(member, component);
    if (polygon == null) return [];
    try {
      if (!outline.touchesOrOverlaps(polygon)) return [];
    } catch {
      return [];
    }
    const centerline = describeMemberGeometry(member, component).centerline;
    if (centerline == null) return [];
    const { from, to } = centerline;
    return [
      { name: member.name, direction: roundAngle(((Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI) % 180) },
    ];
  });
}

function getPlateOptions(state: RootState) {
  return getUniquePlatesInMaterialSetForActiveComponentDesign(state);
}

function describePlates(state: RootState, componentDesign: ComponentDesign) {
  const locked = new Set(componentDesign.lockedPlatePairs);
  const plates = componentDesign.component.platePairs;
  const options = getPlateOptions(state);
  const types = [
    ...new Map(
      plates.map((plate) => [getPlateTypeString(plate.plateType, plate.tangiblePlateTypesForComboPlateType), plate]),
    ).entries(),
  ];
  return {
    plates: plates.map((plate) => ({
      name: plate.name,
      at: { x: round(plate.centerX), y: round(plate.centerY) },
      angle: roundAngle(plate.angleZ),
      size: `${plate.width}x${plate.length}`,
      type: getPlateTypeString(plate.plateType, plate.tangiblePlateTypesForComboPlateType),
      ...(plate.geometryType !== 'Rectangular' ? { shape: plate.geometryType } : {}),
      ...(plate.platePlacement !== 'FrontBack' ? { placement: plate.platePlacement } : {}),
      locked: componentDesign.platingLocked || locked.has(plate.guid),
      members: getMembersUnderPlate(componentDesign, plate),
    })),
    sizesInStock: Object.fromEntries(
      types.map(([type, plate]) => [
        type,
        getPlateDimensionOptions(options, plate)
          .filter((option) => option.geometryType === 'Rectangular')
          .map((option) => `${option.width}x${option.length}`),
      ]),
    ),
  };
}

function parseSize(size: string) {
  const match = /^\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*$/i.exec(size);
  if (match == null) throw new ToolError(`"${size}" isn’t a plate size; give width by length, such as "4x7".`);
  return { width: Number(match[1]), length: Number(match[2]) };
}

/** The stock plate of `size` in the same type as `like`, as the properties panel offers it. */
function findStockPlate(
  state: RootState,
  like: Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'>,
  size: string,
) {
  const { width, length } = parseSize(size);
  const options = getPlateDimensionOptions(getPlateOptions(state), like);
  const option = options.find(
    (candidate) => candidate.width === width && candidate.length === length && candidate.geometryType === 'Rectangular',
  );
  if (option == null)
    throw new ToolError(
      `There is no ${size} ${getPlateTypeString(like.plateType, like.tangiblePlateTypesForComboPlateType)} plate in ` +
        `stock. Sizes in stock: ${options.map((candidate) => `${candidate.width}x${candidate.length}`).join(', ')}.`,
    );
  return option;
}

function readPoint(value: unknown, what: string) {
  const point = value as { x?: unknown; y?: unknown } | null;
  if (point == null || typeof point.x !== 'number' || typeof point.y !== 'number')
    throw new ToolError(`${what} must be {"x": inches, "y": inches}.`);
  return { x: point.x, y: point.y };
}

async function openPlatedTruss(dispatch: AppDispatch, getState: () => RootState, name: string) {
  const componentDesign = await openTruss(dispatch, getState, name);
  if (!isTruss(componentDesign.component)) throw new ToolError('Only trusses have plates.');
  return componentDesign;
}

interface PlateChange {
  readonly plate: string;
  readonly at?: { x: number; y: number };
  readonly angle?: number;
  readonly size?: string;
}

export const plateTools = [
  defineTool<{ truss: string }>({
    name: 'get_plates',
    description:
      'Reads a truss’s plates: each plate’s name (the joint it’s on), center, angle (the direction its length ' +
      'runs, degrees counter-clockwise from horizontal), size as width by length in inches, type, whether it’s ' +
      'locked, and the members it covers with the direction each runs. Also lists the plate sizes in stock for ' +
      'each type. Coordinates are the truss’s: inches from the left end, up from the bottom.',
    inputSchema: { type: 'object', properties: { truss: TRUSS_SCHEMA }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openPlatedTruss(dispatch, getState, requireString(input, 'truss'));
      return { summary: describePlates(getState(), componentDesign) };
    },
  }),

  defineTool<{ truss: string; changes: PlateChange[] }>({
    name: 'edit_plates',
    description:
      'Moves, turns, or resizes plates, as dragging a plate or editing it in its properties does. For each plate ' +
      'give only what changes: `at` its new center, `angle` the direction its length runs (to line a plate up ' +
      'with a member, use the member’s direction from get_plates), and `size` a size in stock. Edited plates are ' +
      'locked. Check them with diagnose_truss, which analyzes the plates as they are. Upgrade and Analyze keeps ' +
      'locked plates but adds its own wherever it finds a joint without one, such as where a moved plate was.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        changes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              plate: { type: 'string', description: 'The plate’s name, as get_plates lists it.' },
              at: POINT_SCHEMA,
              angle: ANGLE_SCHEMA,
              size: SIZE_SCHEMA,
            },
            required: ['plate'],
          },
        },
      },
      required: ['truss', 'changes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openPlatedTruss(dispatch, getState, requireString(input, 'truss'));
      if (!Array.isArray(input.changes) || input.changes.length === 0)
        throw new ToolError('Give at least one change, such as {"plate": "3", "angle": 26.6}.');

      const plates = componentDesign.component.platePairs;
      const edited = input.changes.map((change, index) => {
        const plate = plates.find(
          (candidate) => candidate.name.toLowerCase() === String(change.plate).trim().toLowerCase(),
        );
        if (plate == null)
          throw new ToolError(
            `There is no plate "${change.plate}". The plates are ${plates.map((candidate) => candidate.name).join(', ')}.`,
          );
        const at = change.at == null ? null : readPoint(change.at, `changes[${index}].at`);
        if (change.angle != null && (typeof change.angle !== 'number' || !Number.isFinite(change.angle)))
          throw new ToolError(`changes[${index}].angle must be a number of degrees.`);
        if (at == null && change.angle == null && change.size == null)
          throw new ToolError(`changes[${index}] changes nothing; give at, angle, or size.`);
        return {
          ...plate,
          ...(at != null ? { centerX: at.x, centerY: at.y } : {}),
          ...(change.angle != null ? { angleZ: roundAngle(change.angle) } : {}),
          ...(change.size != null ? findStockPlate(getState(), plate, change.size) : {}),
        };
      });

      await dispatch(changePlatePairs(componentDesign.guid, edited));
      await waitForAppToSettle(getState);
      return { summary: describePlates(getState(), getState().componentDesigns.data[componentDesign.guid]) };
    },
  }),

  defineTool<{ truss: string; at: { x: number; y: number }; size: string; angle?: number; type?: string }>({
    name: 'add_plate',
    description:
      'Adds a plate to a truss, as Insert > Plate does, centered at `at` on the member there, in a size in stock. ' +
      'It takes the truss’s most common plate type unless `type` names another from get_plates. The new plate is ' +
      'locked. Check it with diagnose_truss rather than Upgrade and Analyze, which may add plates of its own.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        at: POINT_SCHEMA,
        size: SIZE_SCHEMA,
        angle: { ...ANGLE_SCHEMA, description: `${ANGLE_SCHEMA.description} Defaults to vertical (90).` },
        type: { type: 'string', description: 'A plate type as get_plates shows it, such as Any20.' },
      },
      required: ['truss', 'at', 'size'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openPlatedTruss(dispatch, getState, requireString(input, 'truss'));
      const at = readPoint(input.at, '"at"');
      const point = new Point2D(at.x, at.y);
      const member = getTrussMembers(componentDesign).find((candidate) =>
        calculateMemberPolygon(candidate, componentDesign.component)?.containsPoint(point),
      );
      if (member == null) throw new ToolError(`No member is at (${at.x}, ${at.y}); center the plate on a joint.`);

      const plates = componentDesign.component.platePairs;
      const options = getPlateOptions(getState());
      const like =
        input.type != null
          ? options.find(
              (option) =>
                getPlateTypeString(option.plateType, option.tangiblePlateTypesForComboPlateType) === input.type,
            )
          : (getMostCommonPlateType(plates) ?? options[0]);
      if (like == null)
        throw new ToolError(
          `There is no plate type "${input.type}" in stock. The types are ` +
            `${[...new Set(options.map((option) => getPlateTypeString(option.plateType, option.tangiblePlateTypesForComboPlateType)))].join(', ')}.`,
        );
      const stock = findStockPlate(getState(), like, requireString(input, 'size'));

      const before = new Set(plates.map((plate) => plate.guid));
      dispatch(startInsertPlatePair(point));
      dispatch(
        updateAndPotentiallyCommitInsertionPlatePair({
          position: point,
          memberGuid: member.guid,
          plateType: stock.plateType,
          tangiblePlateTypesForComboPlateType: stock.tangiblePlateTypesForComboPlateType ?? [],
          plateDimensions: { width: stock.width, length: stock.length, geometryType: stock.geometryType },
          platePlacement: 'FrontBack',
        }),
      );
      await waitForAppToSettle(getState);

      const added = getState().componentDesigns.data[componentDesign.guid].component.platePairs.find(
        (plate) => !before.has(plate.guid),
      );
      if (added == null) {
        dispatch(cancelInsertion());
        throw new ToolError('The app could not add that plate.');
      }
      if (input.angle != null && roundAngle(input.angle) !== roundAngle(added.angleZ)) {
        await dispatch(changePlatePairs(componentDesign.guid, [{ ...added, angleZ: roundAngle(input.angle) }]));
        await waitForAppToSettle(getState);
      }
      return {
        summary: {
          added: added.name,
          ...describePlates(getState(), getState().componentDesigns.data[componentDesign.guid]),
        },
      };
    },
  }),
  defineTool<{ truss: string; plates: string[] }>({
    name: 'delete_plates',
    description:
      'Deletes plates by name, as selecting them and pressing Delete does, for example a duplicate plate on a joint. ' +
      'Upgrade and Analyze puts a plate back on any joint left without one.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, plates: { type: 'array', items: { type: 'string' } } },
      required: ['truss', 'plates'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openPlatedTruss(dispatch, getState, requireString(input, 'truss'));
      if (!Array.isArray(input.plates) || input.plates.length === 0) throw new ToolError('Name at least one plate.');
      const plates = componentDesign.component.platePairs;
      const toDelete = input.plates.map((name) => {
        const plate = plates.find((candidate) => candidate.name.toLowerCase() === String(name).trim().toLowerCase());
        if (plate == null)
          throw new ToolError(
            `There is no plate "${name}". The plates are ${plates.map((candidate) => candidate.name).join(', ')}.`,
          );
        return plate;
      });

      await dispatch(deleteItems(toDelete.map((plate) => ({ type: 'platePair' as const, item: plate }))));
      await waitForAppToSettle(getState);
      return { summary: describePlates(getState(), getState().componentDesigns.data[componentDesign.guid]) };
    },
  }),
];
